import React from 'react';
import { ArrowRight, CheckCircle, Zap, Shield, Users, TrendingUp, RefreshCw, BarChart } from 'lucide-react';
import Layout from '../../layouts/Layout';
import CTA from '../../components/CTA';
import { Link } from 'react-router-dom';

const DigitalTransformationPage = () => {
    const transformationServices = [
        {
            icon: <RefreshCw className="w-6 h-6" />,
            title: "Legacy System Modernisation",
            description: "Transform outdated systems into modern, efficient solutions without disrupting operations.",
            features: ["System assessment", "Migration strategy", "Risk mitigation", "Staff training"]
        },
        {
            icon: <BarChart className="w-6 h-6" />,
            title: "Process Automation",
            description: "Streamline business processes with intelligent automation and workflow optimisation.",
            features: ["Workflow analysis", "Automation design", "Integration planning", "Performance monitoring"]
        },
        {
            icon: <TrendingUp className="w-6 h-6" />,
            title: "Digital Strategy Consulting",
            description: "Strategic guidance for technology adoption and digital transformation roadmaps.",
            features: ["Digital assessment", "Strategy development", "Technology selection", "Implementation planning"]
        },
        {
            icon: <Users className="w-6 h-6" />,
            title: "Change Management",
            description: "Support your team through digital transformation with training and adoption strategies.",
            features: ["Training programmes", "User adoption", "Support systems", "Performance tracking"]
        }
    ];

    const benefits = [
        {
            icon: <Zap className="w-6 h-6" />,
            title: "Increased Efficiency",
            description: "Streamlined processes and automated workflows that reduce manual tasks and improve productivity by up to 40%."
        },
        {
            icon: <Shield className="w-6 h-6" />,
            title: "Enhanced Security",
            description: "Modern security frameworks and compliance measures that protect your business and customer data."
        },
        {
            icon: <TrendingUp className="w-6 h-6" />,
            title: "Improved ROI",
            description: "Measurable business outcomes with cost reductions and revenue improvements from digital initiatives."
        },
        {
            icon: <Users className="w-6 h-6" />,
            title: "Better User Experience",
            description: "Enhanced customer and employee experiences through intuitive digital interfaces and self-service options."
        }
    ];

    const transformationAreas = [
        {
            title: "Customer Experience",
            description: "Transform how customers interact with your business",
            initiatives: [
                "Customer portal development",
                "Self-service platforms",
                "Mobile-first experiences",
                "Personalisation engines",
                "Omnichannel integration",
                "Customer analytics"
            ]
        },
        {
            title: "Operations & Processes",
            description: "Optimise internal operations for maximum efficiency",
            initiatives: [
                "Workflow automation",
                "Document management",
                "Inventory optimisation",
                "Quality assurance systems",
                "Performance dashboards",
                "Resource planning"
            ]
        },
        {
            title: "Data & Analytics",
            description: "Harness the power of data for informed decision-making",
            initiatives: [
                "Data warehouse implementation",
                "Business intelligence dashboards",
                "Predictive analytics",
                "Real-time reporting",
                "Data governance",
                "Machine learning integration"
            ]
        },
        {
            title: "Infrastructure Modernisation",
            description: "Update technology infrastructure for scalability and performance",
            initiatives: [
                "Cloud migration strategy",
                "Microservices architecture",
                "API-first development",
                "Container orchestration",
                "DevOps implementation",
                "Security modernisation"
            ]
        }
    ];

    const process = [
        {
            step: "01",
            title: "Digital Assessment",
            description: "Comprehensive evaluation of current systems, processes, and digital maturity to identify transformation opportunities."
        },
        {
            step: "02",
            title: "Strategy Development",
            description: "Creation of a tailored digital transformation roadmap with clear objectives, timelines, and success metrics."
        },
        {
            step: "03",
            title: "Pilot Implementation",
            description: "Proof-of-concept projects to validate approaches and demonstrate value before full-scale deployment."
        },
        {
            step: "04",
            title: "Phased Rollout",
            description: "Systematic implementation with minimal disruption, including data migration and system integration."
        },
        {
            step: "05",
            title: "Training & Adoption",
            description: "Comprehensive training programmes and change management to ensure successful user adoption."
        },
        {
            step: "06",
            title: "Optimisation & Support",
            description: "Ongoing monitoring, optimisation, and support to maximise the value of digital investments."
        }
    ];

    const industryExpertise = [
        {
            industry: "Manufacturing",
            description: "Industry 4.0 transformation with IoT integration and smart factory solutions",
            outcomes: ["30% efficiency increase", "Predictive maintenance", "Quality automation"]
        },
        {
            industry: "Financial Services",
            description: "Digital banking solutions and regulatory compliance automation",
            outcomes: ["50% faster processing", "Enhanced security", "Regulatory compliance"]
        },
        {
            industry: "Healthcare",
            description: "Patient management systems and telemedicine platform development",
            outcomes: ["Improved patient care", "HIPAA compliance", "Operational efficiency"]
        },
        {
            industry: "Retail & E-commerce",
            description: "Omnichannel experiences and inventory management optimisation",
            outcomes: ["25% revenue increase", "Better customer insights", "Inventory optimisation"]
        }
    ];

    const keyMetrics = [
        "Comprehensive digital maturity assessment",
        "Custom transformation roadmap development",
        "Legacy system migration with zero downtime",
        "Process automation for 40%+ efficiency gains",
        "Staff training and change management support",
        "Ongoing optimisation and performance monitoring",
        "Measurable ROI within 6-12 months",
        "24/7 support throughout transformation journey"
    ];

    return (
        <Layout
            title="Digital Transformation Services"
            description="Digital transformation consulting and implementation services in the UK. Legacy system modernisation, process automation, and strategic technology adoption for business growth."
            keywords="digital transformation UK, legacy system modernisation, process automation, digital strategy consulting, business transformation, technology consulting"
            path="/services/digital-transformation"
        >
            <div className="min-h-screen bg-white">
                {/* Hero Section */}
                <section className="relative pt-32 pb-20 px-6 bg-gradient-to-br from-slate-50 to-slate-100">
                    <div className="max-w-6xl mx-auto">
                        <div className="grid lg:grid-cols-2 gap-12 items-center">
                            <div>
                                <div className="inline-flex items-center px-4 py-2 bg-slate-900 text-white rounded-full text-sm font-medium mb-8">
                                    <RefreshCw className="w-4 h-4 mr-2" />
                                    Digital Transformation
                                </div>
                                
                                <h1 className="text-5xl lg:text-6xl font-bold text-slate-900 mb-8 leading-tight">
                                    Transform Your Business
                                    <span className="block text-slate-700 mt-2">for the Digital Age</span>
                                </h1>
                                
                                <p className="text-xl text-slate-600 mb-10 leading-relaxed">
                                    Modernise your business with strategic digital transformation. We help organisations 
                                    evolve legacy systems, automate processes, and implement modern technologies that drive 
                                    growth, efficiency, and competitive advantage.
                                </p>
                                
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <Link to="/contact" className="bg-slate-900 text-white px-8 py-4 rounded-xl font-semibold hover:bg-slate-800 transition-all hover:shadow-lg flex items-center justify-center">
                                        Start Your Transformation <ArrowRight className="ml-2 w-5 h-5" />
                                    </Link>
                                    <Link to="/schedule-consultation" className="border-2 border-slate-200 text-slate-700 px-8 py-4 rounded-xl font-semibold hover:border-slate-300 hover:bg-slate-50 transition-all">
                                        Get Digital Assessment
                                    </Link>
                                </div>
                            </div>
                            
                            <div className="relative">
                                <div className="bg-white rounded-3xl p-8 shadow-2xl border border-slate-200">
                                    <div className="space-y-6">
                                        <div className="flex items-center justify-between">
                                            <span className="text-slate-600">Efficiency Improvement</span>
                                            <span className="text-slate-900 font-semibold">40%+ Average</span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-slate-600">Implementation Time</span>
                                            <span className="text-slate-900 font-semibold">3-6 Months</span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-slate-600">ROI Timeline</span>
                                            <span className="text-slate-900 font-semibold">6-12 Months</span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-slate-600">Success Rate</span>
                                            <span className="text-slate-900 font-semibold">95%+</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Transformation Services Section */}
                <section className="py-24 bg-white">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-slate-900 mb-6">Digital Transformation Services</h2>
                            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                                Comprehensive solutions to modernise your business and drive digital innovation
                            </p>
                        </div>
                        
                        <div className="grid md:grid-cols-2 gap-8">
                            {transformationServices.map((service, index) => (
                                <div key={index} className="bg-slate-50 rounded-2xl p-8 border border-slate-200 hover:shadow-lg transition-shadow">
                                    <div className="w-16 h-16 bg-slate-900 rounded-xl flex items-center justify-center text-white mb-6">
                                        {service.icon}
                                    </div>
                                    <h3 className="text-2xl font-bold text-slate-900 mb-4">{service.title}</h3>
                                    <p className="text-slate-600 mb-6 leading-relaxed">{service.description}</p>
                                    <div className="grid grid-cols-2 gap-3">
                                        {service.features.map((feature, featureIndex) => (
                                            <div key={featureIndex} className="flex items-center">
                                                <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                                                <span className="text-slate-700 text-sm">{feature}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Benefits Section */}
                <section className="py-24 bg-slate-50">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-slate-900 mb-6">Transformation Benefits</h2>
                            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                                Measurable outcomes that drive business growth and operational excellence
                            </p>
                        </div>
                        
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                            {benefits.map((benefit, index) => (
                                <div key={index} className="bg-white rounded-2xl p-8 border border-slate-200 hover:shadow-lg transition-shadow">
                                    <div className="w-16 h-16 bg-slate-900 rounded-xl flex items-center justify-center text-white mb-6">
                                        {benefit.icon}
                                    </div>
                                    <h3 className="text-xl font-bold text-slate-900 mb-4">{benefit.title}</h3>
                                    <p className="text-slate-600 leading-relaxed">{benefit.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Transformation Areas Section */}
                <section className="py-24 bg-white">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-slate-900 mb-6">Key Transformation Areas</h2>
                            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                                Strategic focus areas that deliver maximum impact for your digital transformation
                            </p>
                        </div>
                        
                        <div className="grid md:grid-cols-2 gap-8">
                            {transformationAreas.map((area, index) => (
                                <div key={index} className="bg-slate-50 rounded-2xl p-8 border border-slate-200 hover:shadow-lg transition-shadow">
                                    <h3 className="text-2xl font-bold text-slate-900 mb-4">{area.title}</h3>
                                    <p className="text-slate-600 mb-6 leading-relaxed">{area.description}</p>
                                    <div className="space-y-3">
                                        {area.initiatives.map((initiative, initiativeIndex) => (
                                            <div key={initiativeIndex} className="flex items-center">
                                                <CheckCircle className="w-4 h-4 text-green-600 mr-3" />
                                                <span className="text-slate-700">{initiative}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Process Section */}
                <section className="py-24 bg-slate-50">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-slate-900 mb-6">Our Transformation Process</h2>
                            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                                A proven methodology that ensures successful digital transformation with minimal disruption
                            </p>
                        </div>
                        
                        <div className="space-y-8">
                            {process.map((step, index) => (
                                <div key={index} className="flex items-start">
                                    <div className="flex-shrink-0 w-16 h-16 bg-slate-900 rounded-xl flex items-center justify-center text-white font-bold text-lg mr-6">
                                        {step.step}
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-2xl font-bold text-slate-900 mb-4">{step.title}</h3>
                                        <p className="text-lg text-slate-600 leading-relaxed">{step.description}</p>
                                    </div>
                                    {/* {index < process.length - 1 && (
                                        <ChevronRight className="w-6 h-6 text-slate-400 ml-6" />
                                    )} */}
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Industry Expertise Section */}
                <section className="py-24 bg-white">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-slate-900 mb-6">Industry Expertise</h2>
                            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                                Sector-specific digital transformation solutions with proven results
                            </p>
                        </div>
                        
                        <div className="grid md:grid-cols-2 gap-8">
                            {industryExpertise.map((industry, index) => (
                                <div key={index} className="bg-slate-50 rounded-2xl p-8 border border-slate-200 hover:shadow-lg transition-shadow">
                                    <h3 className="text-2xl font-bold text-slate-900 mb-4">{industry.industry}</h3>
                                    <p className="text-slate-600 mb-6 leading-relaxed">{industry.description}</p>
                                    <div className="grid grid-cols-1 gap-3">
                                        {industry.outcomes.map((outcome, outcomeIndex) => (
                                            <div key={outcomeIndex} className="flex items-center">
                                                <TrendingUp className="w-4 h-4 text-green-600 mr-3" />
                                                <span className="text-slate-700 font-medium">{outcome}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Key Metrics Section */}
                <section className="py-24 bg-slate-50">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="grid lg:grid-cols-2 gap-16 items-center">
                            <div>
                                <h2 className="text-4xl font-bold text-slate-900 mb-8">What You Get</h2>
                                <p className="text-xl text-slate-600 mb-10 leading-relaxed">
                                    Our digital transformation services provide end-to-end support that ensures successful 
                                    modernisation with measurable business outcomes and long-term value.
                                </p>
                                
                                <div className="space-y-4">
                                    {keyMetrics.map((metric, index) => (
                                        <div key={index} className="flex items-start">
                                            <CheckCircle className="w-6 h-6 text-green-600 mr-4 flex-shrink-0 mt-0.5" />
                                            <span className="text-slate-700 text-lg">{metric}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            
                            <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-lg">
                                <h3 className="text-2xl font-bold text-slate-900 mb-6">Transformation Outcomes</h3>
                                <div className="space-y-6">
                                    <div className="flex items-center justify-between">
                                        <span className="text-slate-600">Process Efficiency</span>
                                        <span className="text-slate-900 font-semibold">+40% Average</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-slate-600">Cost Reduction</span>
                                        <span className="text-slate-900 font-semibold">20-30%</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-slate-600">Customer Satisfaction</span>
                                        <span className="text-slate-900 font-semibold">+25% Increase</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-slate-600">Employee Productivity</span>
                                        <span className="text-slate-900 font-semibold">+35% Boost</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-slate-600">Revenue Growth</span>
                                        <span className="text-slate-900 font-semibold">15-25%</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Performance Stats */}
                <section className="py-24 bg-white">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="bg-slate-900 rounded-3xl p-12 text-white">
                            <div className="text-center mb-12">
                                <h2 className="text-4xl font-bold mb-6">Transformation Success Metrics</h2>
                                <p className="text-xl text-slate-300 max-w-3xl mx-auto">
                                    Proven results from digital transformation projects across various industries
                                </p>
                            </div>
                            
                            <div className="grid md:grid-cols-4 gap-8 text-center">
                                <div>
                                    <div className="text-4xl font-bold mb-2">95%</div>
                                    <div className="text-slate-300">Success Rate</div>
                                </div>
                                <div>
                                    <div className="text-4xl font-bold mb-2">40%</div>
                                    <div className="text-slate-300">Efficiency Gain</div>
                                </div>
                                <div>
                                    <div className="text-4xl font-bold mb-2">6 Months</div>
                                    <div className="text-slate-300">Average ROI</div>
                                </div>
                                <div>
                                    <div className="text-4xl font-bold mb-2">24/7</div>
                                    <div className="text-slate-300">Support</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <CTA />
            </div>
        </Layout>
    );
};

export default DigitalTransformationPage;